import { useEffect, useMemo, useRef, useState } from 'react'
import { useRoster, type TabKey } from '../lib/roster'
import {
  DOCX_MIME,
  XLSX_MIME,
  buildLabelsDocxFrom,
  buildWorkbook,
  download,
  householdGroups,
  labelsFor,
  sheetsNeeded,
  split,
  stamp,
} from '../lib/exporting'

/**
 * Export whatever is on screen.
 *
 * The numbers are shown BEFORE either button is pressed — "347 of 370 have an
 * address" — because the gap between those two figures is the thing Renee
 * needs to notice. A run of labels that quietly comes out 23 short is the
 * failure this is here to prevent.
 */
export default function ExportMenu({ tab }: { tab: TabKey }) {
  const { visible, agents } = useRoster()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [perHousehold, setPerHousehold] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  const rows = open ? visible(tab) : []
  const selected = split(rows)
  const roster = agents ?? []

  // The number of labels, taken from the labels themselves rather than from
  // the number of people who have an address. Only computed while the menu is
  // open, which is the only time it is on screen.
  const labelCount = useMemo(
    () => (open ? labelsFor(selected, roster, { perHousehold }).length : 0),
    [open, rows, roster, perHousehold],
  )
  /**
   * Two different numbers, and saying the wrong one is misleading: the agents
   * who share an address with somebody, and the envelopes grouping would save.
   * They are not the same — three people at one address are three sharers and
   * two envelopes saved.
   */
  const households = useMemo(() => (open ? householdGroups(selected) : []), [open, rows])
  const sharing = households.reduce((n, g) => (g.names.length > 1 ? n + g.names.length : n), 0)
  const envelopesSaved = selected.mailable.length - households.length

  /**
   * Grouping is a per-run choice, not a setting. Left ticked from a Christmas
   * card run it would quietly group a licence-renewal run a week later, and
   * when nobody in the selection shares an address the tick box is not even on
   * screen to notice.
   */
  useEffect(() => {
    setPerHousehold(false)
  }, [tab])
  useEffect(() => {
    if (sharing === 0 && perHousehold) setPerHousehold(false)
  }, [sharing, perHousehold])

  const run = (what: 'sheet' | 'labels') => {
    setBusy(what)
    setFailed(null)
    // Next tick, so the button can repaint as "Working…" before the main thread
    // is taken for the fraction of a second it costs to build a file.
    window.setTimeout(() => {
      try {
        if (what === 'sheet') {
          download(
            buildWorkbook(selected, roster),
            `agent-care-${selected.agents.length}-agents-${stamp()}.xlsx`,
            XLSX_MIME,
          )
        } else {
          const labels = labelsFor(selected, roster, { perHousehold })
          download(
            buildLabelsDocxFrom(labels),
            `agent-care-labels-${labels.length}-${stamp()}.docx`,
            DOCX_MIME,
          )
        }
        setOpen(false)
      } catch (err) {
        // Without this, a failure looks exactly like a success minus the file:
        // the menu closes, nothing downloads, and the natural next move is to
        // click again. A thrown error inside a timer never reaches React.
        console.error('export failed', err)
        setFailed(
          'The file could not be built, and nothing was saved. Tell Chris which filters were on.',
        )
      } finally {
        setBusy(null)
      }
    }, 30)
  }

  return (
    <div className="exportwrap" ref={box}>
      <button className="btn sm" onClick={() => setOpen((v) => !v)}>
        Export
      </button>

      {open && (
        <div className="exportmenu">
          {selected.agents.length === 0 ? (
            <p className="hint">Nothing to export — no agents match.</p>
          ) : (
            <>
              <p className="cnt">
                {selected.agents.length} {selected.agents.length === 1 ? 'agent' : 'agents'} selected
              </p>
              <p className="hint">
                {selected.mailable.length} of {selected.agents.length} have an address
                {selected.unmailable.length > 0 && (
                  <>
                    {' · '}
                    <strong>{selected.unmailable.length}</strong> without
                  </>
                )}
              </p>

              {failed && <p className="error">{failed}</p>}

              <button className="btn sm" disabled={busy !== null} onClick={() => run('sheet')}>
                {busy === 'sheet' ? 'Working…' : 'Spreadsheet (Excel)'}
              </button>
              <p className="hint">
                Every column, including emails and phone numbers
                {selected.unmailable.length > 0
                  ? `, plus a second tab listing the ${selected.unmailable.length} with no address.`
                  : '.'}
              </p>

              <button
                className="btn sm"
                disabled={busy !== null || labelCount === 0}
                onClick={() => run('labels')}
              >
                {busy === 'labels' ? 'Working…' : 'Mailing labels (Word)'}
              </button>
              {sharing > 0 && (
                <>
                  <label className="chk stack">
                    <input
                      type="checkbox"
                      checked={perHousehold}
                      onChange={(e) => setPerHousehold(e.target.checked)}
                    />
                    One label per household
                  </label>
                  <p className="hint">
                    {sharing} agents share an address with someone else here, so grouping them saves{' '}
                    {envelopesSaved} envelope{envelopesSaved === 1 ? '' : 's'}. Off by default — a
                    licence should be addressed to the person it belongs to. Addresses are matched
                    exactly as stored, so two people who typed theirs differently still get separate
                    labels.
                  </p>
                </>
              )}
              <p className="hint">
                {labelCount === 0
                  ? 'Nobody in this selection has an address.'
                  : `${labelCount} labels on ${sheetsNeeded(labelCount)} sheet${
                      sheetsNeeded(labelCount) === 1 ? '' : 's'
                    } of Avery 5160.`}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  )
}
